import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { HealthService } from '../../services/health.service';
import {
  HealthStatusResponse,
  ServiceHealthResult,
  ServiceStatus
} from '../../models/health-status.model';

/** View model do grupo "Mercado Livre" (agrupa os dois checks com sub-detalhes). */
export interface MercadoLivreGroup {
  displayStatus: ServiceStatus;
  apiReachable?: ServiceHealthResult;
  authValid?: ServiceHealthResult;
}

/**
 * LED de saúde no header. Faz polling de `GET /api/health/status` a cada 60s, exibe a cor
 * do status consolidado e, ao ser acionado, abre um popover com o detalhe de cada dependência
 * (Mercado Livre aparece agrupado, mas com os dois checks técnicos separados).
 */
@Component({
  selector: 'app-health-indicator',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, TranslocoModule],
  templateUrl: './health-indicator.component.html',
  styleUrl: './health-indicator.component.scss'
})
export class HealthIndicatorComponent implements OnInit, OnDestroy {
  private static readonly POLL_INTERVAL_MS = 60000;

  health: HealthStatusResponse = HealthService.unknownResponse();
  detailsOpen = false;

  private pollSub?: Subscription;

  constructor(
    private readonly healthService: HealthService,
    private readonly transloco: TranslocoService,
    @Inject(PLATFORM_ID) private readonly platformId: Object
  ) { }

  ngOnInit(): void {
    // Não fazer polling durante o SSR — apenas no browser.
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.pollSub = timer(0, HealthIndicatorComponent.POLL_INTERVAL_MS)
      .pipe(switchMap(() => this.healthService.getStatus()))
      .subscribe(result => {
        this.health = result;
      });
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
  }

  get status(): ServiceStatus {
    return this.health.status;
  }

  get isHealthy(): boolean {
    return this.health.status === 'Healthy';
  }

  /** Classe CSS do LED principal, mapeada do status consolidado. */
  get ledClass(): string {
    return this.ledClassFor(this.health.status);
  }

  get tooltip(): string {
    return this.transloco.translate(this.isHealthy ? 'health.tooltipOk' : 'health.tooltipIssue');
  }

  /** Texto resumido exibido na pill do header. */
  get summaryLabel(): string {
    return this.transloco.translate('health.summary.' + this.statusKey(this.health.status));
  }

  /** Mensagem traduzida do cabeçalho do popover (substitui o texto do backend). */
  get headerMessage(): string {
    if (this.isHealthy) return this.transloco.translate('health.message.healthy');
    if (this.health.status === 'Unknown') return this.transloco.translate('health.message.unknown');
    return this.transloco.translate('health.message.issue');
  }

  /** Abre os detalhes ao passar o mouse quando há instabilidade (FR-029). */
  onMouseEnter(): void {
    if (!this.isHealthy) {
      this.detailsOpen = true;
    }
  }

  get mongoDb(): ServiceHealthResult | undefined {
    return this.findService('MongoDB');
  }

  get rabbitMq(): ServiceHealthResult | undefined {
    return this.findService('RabbitMQ');
  }

  /** Agrupa os dois checks do Mercado Livre em um único item visual com sub-detalhes. */
  get mercadoLivre(): MercadoLivreGroup {
    const apiReachable = this.findService('MercadoLivreApiReachable');
    const authValid = this.findService('MercadoLivreAuthenticationValid');

    return {
      displayStatus: this.getMercadoLivreDisplayStatus(this.health.services),
      apiReachable,
      authValid
    };
  }

  /** Pior status entre os dois checks do Mercado Livre. */
  getMercadoLivreDisplayStatus(services: ServiceHealthResult[]): ServiceStatus {
    const mlStatuses = services
      .filter(s => s.name === 'MercadoLivreApiReachable' || s.name === 'MercadoLivreAuthenticationValid')
      .map(s => s.status);

    return this.worstStatus(mlStatuses);
  }

  /** Classe CSS de LED para um status arbitrário (usado nos sub-itens). */
  ledClassFor(status: ServiceStatus | undefined): string {
    switch (status) {
      case 'Healthy': return 'health-led--healthy';
      case 'Degraded': return 'health-led--degraded';
      case 'Unhealthy': return 'health-led--unhealthy';
      default: return 'health-led--unknown';
    }
  }

  /** Rótulo amigável e traduzido para um status — sem expor termos técnicos na UI. */
  statusLabel(status: ServiceStatus | undefined): string {
    return this.transloco.translate('health.status.' + this.statusKey(status));
  }

  /** Mapeia o status para o sufixo da chave i18n. */
  private statusKey(status: ServiceStatus | undefined): string {
    switch (status) {
      case 'Healthy': return 'healthy';
      case 'Degraded': return 'degraded';
      case 'Unhealthy': return 'unhealthy';
      default: return 'unknown';
    }
  }

  toggleDetails(): void {
    this.detailsOpen = !this.detailsOpen;
  }

  private findService(name: string): ServiceHealthResult | undefined {
    return this.health.services.find(s => s.name === name);
  }

  private worstStatus(statuses: ServiceStatus[]): ServiceStatus {
    // Severidade crescente: Healthy < Unknown < Degraded < Unhealthy.
    const severity: Record<ServiceStatus, number> = {
      Healthy: 0,
      Unknown: 1,
      Degraded: 2,
      Unhealthy: 3
    };

    if (statuses.length === 0) {
      return 'Unknown';
    }

    return statuses.reduce(
      (worst, current) => (severity[current] > severity[worst] ? current : worst),
      statuses[0]
    );
  }
}
